import React, { useState, useEffect } from "react";
import {
  Card,
  Container,
  Row,
  Col,
  Table,
  Spinner,
  Button,
  Badge,
} from "react-bootstrap";
import { 
  getDashboardStats, 
  getUpcomingBirthdays,
  getUpcomingAppraisals,
  getUpcomingLeaves,
  getActiveEmployeesWithNoticePeriod
} from "../services/dashboardService";
import { getEmployees } from "../services/employeeService";
import Layout from "../components/Layout";
import TopBar from "../components/TopBar";
import { FaUser, FaCar, FaArrowRight, FaUserClock, FaCalendarCheck, FaBell } from "react-icons/fa";
import moment from 'moment';
import ExpiringEmployees from '../components/ExpiringEmployees';

const AdminDashboard = () => {
  // Initialize state
  const [stats, setStats] = useState({
    total_employees: 0,
    todays_attendance: 0,
    total_vehicles: 0,
    total_licenses: 0,
  });

  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [upcomingAppraisals, setUpcomingAppraisals] = useState([]);
  const [upcomingLeaves, setUpcomingLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeTypeCounts, setEmployeeTypeCounts] = useState({});
  const [noticePeriodEmployees, setNoticePeriodEmployees] = useState([]);
  const [loadingNoticePeriod, setLoadingNoticePeriod] = useState(true);

  const EMPLOYEE_TYPES = [
    'Permanent',
    'Contract',
    'Intern',
    'Trainee',
    'Probation',
    'Freelancer',
    'Part Time'
  ];
  const EMPLOYEE_TYPE_COLORS = [
    '#4e73df', // Permanent
    '#36b9cc', // Contract
    '#1cc88a', // Intern
    '#f6c23e', // Trainee
    '#e74a3b', // Probation
    '#858796', // Freelancer
    '#fd7e14'  // Part Time
  ];

  useEffect(() => {
    loadDashboardData();
    loadEmployeeTypes();
  }, []);

  const loadNoticePeriodEmployees = async () => {
    try {
      setLoadingNoticePeriod(true);
      const response = await getActiveEmployeesWithNoticePeriod();
      setNoticePeriodEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching notice period employees:', error);
      setNoticePeriodEmployees([]);
    } finally {
      setLoadingNoticePeriod(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();
      
      console.log('Fetching dashboard data for month:', currentMonth, 'year:', currentYear);
      
      // Fetch data in parallel
      await Promise.all([
        loadNoticePeriodEmployees(),
        // Other data fetches will be added here
      ]);
      
      // First fetch leaves separately to better handle and debug the response
      const leavesRes = await getUpcomingLeaves(currentMonth, currentYear);
      console.log('Leaves API response:', leavesRes);
      
      // Process leaves data
      let leavesData = [];
      if (leavesRes?.data) {
        leavesData = Array.isArray(leavesRes.data) ? leavesRes.data : [];
        console.log('Leaves data:', leavesData);
        
        // Ensure we have the expected properties
        leavesData = leavesData.map(leave => {
          // Handle both nested and flat employee/type structures
          const employee = leave.employee || (leave.employee_id ? { name: 'Employee ' + leave.employee_id } : { name: 'Unknown' });
          const type = leave.type || (leave.leave_type_id ? { name: 'Type ' + leave.leave_type_id } : { name: 'N/A' });
          
          return {
            ...leave,
            employee: typeof employee === 'object' ? employee : { name: String(employee) },
            type: typeof type === 'object' ? type : { name: String(type) },
            start_date: leave.start_date || leave.startDate,
            end_date: leave.end_date || leave.endDate || leave.start_date || leave.startDate,
            status: (leave.status || 'pending').toLowerCase()
          };
        });
      }
      
      console.log('Processed leaves:', leavesData);
      setUpcomingLeaves(leavesData);
      
      // Fetch other data in parallel
      const [
        statsRes, 
        birthdaysRes,
        appraisalsRes
      ] = await Promise.all([
        getDashboardStats(),
        getUpcomingBirthdays(5), // Get birthdays in next 5 days
        getUpcomingAppraisals(15) // Get appraisals in next 15 days
      ]);

      console.log('Dashboard stats:', statsRes.data);
      setStats(statsRes.data);
 
      // Handle birthdays
      const birthdaysData = Array.isArray(birthdaysRes?.data?.data) ? 
        birthdaysRes.data.data : [];
      console.log('Upcoming birthdays:', birthdaysData);
      setUpcomingBirthdays(birthdaysData);
      
      // Handle appraisals
      const appraisalsData = Array.isArray(appraisalsRes?.data) ? 
        appraisalsRes.data : [];
      console.log('Upcoming appraisals:', appraisalsData);
      setUpcomingAppraisals(appraisalsData);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeTypes = async () => {
    try {
      const res = await getEmployees();
      const employees = res.data || [];
      const counts = {};
      employees.forEach(emp => {
        // Only count active employees
        if (emp.status === 'active') {
          const type = emp.employee_type || 'Unknown';
          counts[type] = (counts[type] || 0) + 1;
        }
      });
      setEmployeeTypeCounts(counts);
    } catch (err) {
      console.error('Error loading employee types:', err);
      setEmployeeTypeCounts({});
    }
  };

  const StatCard = ({ icon, title, value, color }) => (
    <Card className="mb-4" style={{ backgroundColor: color }}>
      <Card.Body>
        <div className="d-flex align-items-center">
          <div className="me-3">
            <span className="fs-1" style={{ color: "white" }}>{icon}</span>
          </div>
          <div>
            <Card.Title className="text-white mb-1">{title}</Card.Title>
            <Card.Text className="h2 mb-0 text-white">{value}</Card.Text>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Layout>
      <TopBar title="Dashboard" />
      <Container fluid className="mt-4" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading Dashboard...</p>
          </div>
        ) : (
          <>
            {/* Stats Row */}
            <Row className="g-4 mb-4">
              <Col md={3} sm={6}>
                <StatCard
                  icon={<FaUser />}
                  title="Total Employees"
                  value={stats.total_employees}
                  color="#6c8dfa"
                  bgColor="#edf2ff"
                />
              </Col>
          
              {EMPLOYEE_TYPES.map((type, idx) => (
                <Col md={3} sm={6} key={type}>
                  <StatCard
                    icon={<FaUser />}
                    title={type}
                    value={employeeTypeCounts[type] || 0}
                    color={EMPLOYEE_TYPE_COLORS[idx]}
                  />
                </Col>
              ))}
            </Row>

            {/* Attendance Row */}
            <Row className="g-4 mb-4">
              <Col md={3} sm={6}>
                <StatCard
                  icon={<FaCalendarCheck />}
                  title="Today's Attendance"
                  value={stats.todays_attendance}
                  color="#36b9cc"
                  bgColor="#e3fafc"
                />
              </Col>
              <Col md={3} sm={6}>
                <StatCard
                  icon={<FaUserClock />}
                  title="Absent Today"
                  value={stats.on_leave || 0}
                  color="#e74a3b"
                  bgColor="#fdecea"
                />
              </Col>
            </Row>

            <Row className="g-4">
              {/* Upcoming Leaves This Month */}
              <Col md={6}>
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center bg-primary text-white">
                    <h6 className="mb-0">
                      <FaCalendarCheck className="me-2" />
                      Upcoming Leaves This Month
                    </h6>
                    <Badge bg="light" text="dark" pill>
                      {upcomingLeaves.length} {upcomingLeaves.length === 1 ? 'Leave' : 'Leaves'}
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      {upcomingLeaves.length > 0 ? (
                        <Table hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Employee</th>
                              <th>Type</th>
                              <th>From</th>
                              <th>To</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {upcomingLeaves.map((leave, index) => (
                              <tr key={index}>
                                <td>{leave.employee?.name || 'N/A'}</td>
                                <td>{leave.type?.name || 'N/A'}</td>
                                <td>{moment(leave.start_date).format('MMM D')}</td>
                                <td>{moment(leave.end_date).format('MMM D, YYYY')}</td>
                                <td>
                                  <span className={`badge bg-${{
                                    'approved': 'success',
                                    'pending': 'warning',
                                    'rejected': 'danger'
                                  }[leave.status] || 'secondary'}`}>
                                    {leave.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          No upcoming leaves this month
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Upcoming Appraisals */}
              <Col md={6}>
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center bg-warning text-dark">
                    <h6 className="mb-0">
                      <FaUserClock className="me-2" />
                      Upcoming Appraisals
                    </h6>
                    <Badge bg="light" text="dark" pill>
                      {upcomingAppraisals.length} {upcomingAppraisals.length === 1 ? 'Appraisal' : 'Appraisals'}
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {upcomingAppraisals.length > 0 ? (
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Employee</th>
                            <th>Department</th>
                            <th>Appraisal Date</th>
                            <th>In</th>
                          </tr>
                        </thead>
                        <tbody>
                          {upcomingAppraisals.map((appraisal) => (
                            <tr key={appraisal.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <FaUser className="me-2 text-warning" />
                                  {appraisal.employee_name}
                                </div>
                              </td>
                              <td>{appraisal.department || 'N/A'}</td>
                              <td>{moment(appraisal.next_appraisal_date).format('MMM D, YYYY')}</td>
                              <td>
                                <Badge bg="warning" text="dark">
                                  {appraisal.days_remaining} left
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        No upcoming appraisals in the next 5 days
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Upcoming Birthdays */}
              <Col md={6}>
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center bg-info text-white">
                    <h6 className="mb-0">
                      <FaUser className="me-2" />
                      Upcoming Birthdays
                    </h6>
                    <Badge bg="light" text="dark" pill>
                      {upcomingBirthdays.length} {upcomingBirthdays.length === 1 ? 'Birthday' : 'Birthdays'}
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {upcomingBirthdays.length > 0 ? (
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Employee</th>
                            <th>Department</th>
                            <th>Date</th>
                            <th>In</th>
                          </tr>
                        </thead>
                        <tbody>
                          {upcomingBirthdays.map((emp) => (
                            <tr key={emp.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <FaUser className="me-2 text-primary" />
                                  {emp.name}
                                </div>
                              </td>
                              <td>{emp.department || 'N/A'}</td>
                              <td>{moment(emp.dob).format('MMM D')}</td>
                              <td>
                                <Badge bg="info">
                                  {emp.days_until_birthday} {emp.days_until_birthday === 1 ? 'day' : 'days'} left
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        No upcoming birthdays in the next 5 days
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Expiring Contracts */}
              <Col md={6}>
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <FaUserClock className="me-2" />
                      Expiring Contracts
                    </h6>
                    <Badge bg="light" text="dark">
                      Next 15 days
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <ExpiringEmployees />
                  </Card.Body>
                  <Card.Footer className="text-end">
                    <a href="/employees?filter=expiring" className="btn btn-sm btn-link p-0">
                      View All <FaArrowRight size={12} className="ms-1" />
                    </a>
                  </Card.Footer>
                </Card>
              </Col>

              {/* Active Employees on Notice Period */}
              <Col md={6}>
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center bg-warning text-dark">
                    <h6 className="mb-0">
                      <FaBell className="me-2" />
                      Active Employees on Notice Period
                    </h6>
                    <Badge bg="light" text="dark" pill>
                      {noticePeriodEmployees.length} {noticePeriodEmployees.length === 1 ? 'Employee' : 'Employees'}
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {loadingNoticePeriod ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                      </div>
                    ) : noticePeriodEmployees.length > 0 ? (
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Employee</th>
                            <th>Department</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {noticePeriodEmployees.map((emp) => (
                            <tr key={emp.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <FaUser className="me-2" />
                                  {emp.name}
                                </div>
                              </td>
                              <td>{emp.department || 'N/A'}</td>
                              <td>{moment(emp.notice_period_start_date).format('MMM D, YYYY')}</td>
                              <td>{moment(emp.notice_period_end_date).format('MMM D, YYYY')}</td>
                              <td>
                                <Badge bg={emp.status === 'Active' ? 'success' : 'secondary'}>
                                  {emp.status} {emp.status === 'Active' ? `(${emp.days_remaining} days left)` : ''}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        No active employees on notice period
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

         
          </>
        )}
      </Container>
    </Layout>
  );
};

export default AdminDashboard;
